'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { encodeAssetUrl } from '@/app/utils/stringUtils';
import { getFirstCategoryName } from '@/components/content-types/collection-type/utils';

function timeAgo(dateValue) {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'week', seconds: 604800 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 }
    ];
    for (const { label, seconds: s } of intervals) {
        const count = Math.floor(seconds / s);
        if (count >= 1) return `${count} ${label}${count > 1 ? 's' : ''} ago`;
    }
    return 'just now';
}

function formatDuration(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min`;
    return `${s}s`;
}

function useVideoDuration(identifier) {
    const [duration, setDuration] = useState(null);

    useEffect(() => {
        if (!identifier) return;
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = `/dA/${identifier}`;
        const onLoaded = () => setDuration(formatDuration(video.duration));
        video.addEventListener('loadedmetadata', onLoaded);
        return () => {
            video.removeEventListener('loadedmetadata', onLoaded);
            video.src = '';
        };
    }, [identifier]);

    return duration;
}

export default function VideoCard({ contentlet }) {
    const { thumbnail, thumbnailAlt, title, urlTitle, publishDate, categories, identifier } =
        contentlet;

    const img = Array.isArray(thumbnail) ? thumbnail[0] : thumbnail;
    let src;
    if (img && typeof img === 'object' && img.identifier) {
        src = encodeAssetUrl(`/dA/${img.identifier}`);
    } else if (typeof img === 'string') {
        src = encodeAssetUrl(img);
    }

    const cleanTitle = title?.replace(/\.[^/.]+$/, '') ?? '';
    const relativeDate = timeAgo(publishDate);
    const firstCategory = getFirstCategoryName(categories) || null;
    const duration = useVideoDuration(identifier);

    return (
        <Link href={`/videos/${urlTitle}`} className="group flex gap-3">
            <div className="relative aspect-video w-36 shrink-0 overflow-hidden rounded-lg">
                {src ? (
                    <Image
                        src={src}
                        alt={thumbnailAlt || cleanTitle}
                        fill
                        className="object-cover transition-transform duration-200 group-hover:scale-105"
                        sizes="160px"
                    />
                ) : (
                    <div className="size-full bg-blue-900" />
                )}
            </div>
            <div className="flex flex-col gap-1">
                <p className="line-clamp-3 text-sm font-medium leading-snug text-white">
                    {cleanTitle}
                </p>
                {duration && <span className="text-xs text-custom-transparent">{duration}</span>}
                <div className="flex flex-wrap items-center gap-1 text-xs text-custom-transparent">
                    {firstCategory && (
                        <span className="font-semibold text-fuschia-500">{firstCategory}</span>
                    )}
                    {firstCategory && relativeDate && (
                        <span className="text-custom-transparent">·</span>
                    )}
                    {relativeDate && <span>{relativeDate}</span>}
                </div>
            </div>
        </Link>
    );
}
